import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Upload, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GENRES } from "@/lib/genres";

const empty = { title: "", author: "", publisher: "", genre: "Aventura", price: "", synopsis: "", image_url: "", stock: "" };

export default function BookFormDialog({ open, onOpenChange, book, onSaved }) {
  const [form, setForm] = useState(empty);
  const [cost, setCost] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(book ? {
        title: book.title || "", author: book.author || "", publisher: book.publisher || "",
        genre: book.genre || "Aventura", price: String(book.price ?? ""),
        synopsis: book.synopsis || "", image_url: book.image_url || "",
        stock: book.stock === null || book.stock === undefined ? "" : String(book.stock),
      } : empty);
      setCost(book && book.cost !== null && book.cost !== undefined ? String(book.cost) : "");
    }
  }, [open, book]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("image_url", data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const valid = form.title.trim() && form.author.trim() && form.synopsis.trim() &&
    form.image_url && Number(form.price) >= 0 && form.price !== "";

  const handleSave = async () => {
    if (!valid) {
      toast.error("Completa todos los campos y sube una foto.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(), author: form.author.trim(), publisher: form.publisher.trim(),
      genre: form.genre, price: Number(form.price), synopsis: form.synopsis.trim(), image_url: form.image_url,
      stock: form.stock === "" ? null : Math.max(0, parseInt(form.stock, 10) || 0),
      cost: cost === "" ? 0 : Math.max(0, Number(cost) || 0),
    };
    try {
      if (book?.id) await api.put(`/admin/books/${book.id}`, payload);
      else await api.post("/admin/books", payload);
      toast.success(book?.id ? "Libro actualizado" : "Libro agregado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el libro");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] focus:ring-4 focus:ring-[var(--lc-primary)]/15 outline-none transition-colors p-3 bg-[var(--lc-bg)] text-[var(--lc-ink)]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[92vh] overflow-y-auto" data-testid="book-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)]">
            {book?.id ? "Editar libro" : "Agregar libro"}
          </DialogTitle>
          <DialogDescription className="sr-only">Formulario para {book?.id ? "editar" : "agregar"} un libro del catálogo</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-[160px,1fr] gap-5 mt-2">
          {/* Foto */}
          <div>
            <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Foto</label>
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              className="relative w-full aspect-[2/3] rounded-xl border-2 border-dashed border-[var(--lc-border)] bg-[var(--lc-cream)] grid place-items-center overflow-hidden cursor-pointer hover:border-[var(--lc-primary)] transition-colors"
              data-testid="book-image-dropzone"
            >
              {form.image_url ? (
                <img src={form.image_url} alt="Portada" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-[var(--lc-slate)] p-2">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
                  <span className="text-xs">Subir portada</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/70 grid place-items-center">
                  <Loader2 className="w-7 h-7 animate-spin text-[var(--lc-primary)]" />
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="book-image-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg border border-[var(--lc-border)] py-2 text-sm font-semibold text-[var(--lc-ink)] hover:bg-[var(--lc-cream)]"
            >
              <Upload className="w-4 h-4" /> {form.image_url ? "Cambiar" : "Subir"}
            </button>
          </div>

          {/* Campos */}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Título</label>
              <input className={field} data-testid="book-title-input" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Ej: Cien Años de Soledad" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Autor</label>
                <input className={field} data-testid="book-author-input" value={form.author} onChange={(e) => update("author", e.target.value)} placeholder="Nombre del autor" />
              </div>
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Editorial</label>
                <input className={field} data-testid="book-publisher-input" value={form.publisher} onChange={(e) => update("publisher", e.target.value)} placeholder="Editorial" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Género</label>
                <Select value={form.genre} onValueChange={(v) => update("genre", v)}>
                  <SelectTrigger className="p-3 h-auto rounded-xl border-2 border-[var(--lc-border)] bg-[var(--lc-bg)]" data-testid="book-genre-select">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENRES.map((g) => (
                      <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Precio (COP)</label>
                <input className={field} type="number" min="0" data-testid="book-price-input" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="35000" />
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Sinopsis</label>
              <textarea className={`${field} min-h-[110px] resize-y`} data-testid="book-synopsis-input" value={form.synopsis} onChange={(e) => update("synopsis", e.target.value)} placeholder="Descripción del libro…" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Costo del libro (COP)</label>
                <input
                  className={field}
                  type="number"
                  min="0"
                  step="500"
                  data-testid="book-cost-input"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="Lo que te cuesta el libro"
                />
                <p className="text-xs text-[var(--lc-slate)] mt-1">Se usa para calcular la ganancia (precio − costo).</p>
              </div>
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] mb-1 block">Existencias (stock)</label>
                <input
                  className={field}
                  type="number"
                  min="0"
                  data-testid="book-stock-input"
                  value={form.stock}
                  onChange={(e) => update("stock", e.target.value)}
                  placeholder="Ej: 10 (vacío = sin control de stock)"
                />
                <p className="text-xs text-[var(--lc-slate)] mt-1">Si llega a 0, el libro aparece "Agotado" y el cliente podrá encargarlo.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={() => onOpenChange(false)} className="rounded-full px-5 py-2.5 font-semibold text-[var(--lc-slate)] hover:bg-[var(--lc-cream)]" data-testid="book-form-cancel">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            data-testid="book-form-save"
            className="flex items-center gap-2 rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white px-6 py-2.5 font-display font-extrabold transition-colors disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {book?.id ? "Guardar cambios" : "Agregar libro"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
